// Types based on the OpenAPI spec
export interface InvoiceListItem {
  id: number;
  invoice_number: string;
  date: string;
  amount: number;
  currency: string;
  type: 'invoice' | 'credit_note';
  status: 'not_paid' | 'in_payment' | 'partial' | 'paid' | 'reversed';
  status_label: string;
  status_color: 'green' | 'red' | 'yellow';
}

export interface InvoiceListResponse {
  total: number;
  page: number;
  page_size: number;
  items: InvoiceListItem[];
}

export type InvoiceType = 'invoice' | 'credit_note' | 'all';

// API Base URL
const API_BASE_URL = 'https://moncopieur.cloud-erp.ma';

/**
 * Get invoices list with pagination and filtering
 */
export const getInvoices = async (
  token: string,
  type: InvoiceType = 'all',
  page: number = 1,
  pageSize: number = 20
): Promise<InvoiceListResponse> => {
  console.log('Fetching invoices:', { type, page, pageSize });
  
  try {
    const params = new URLSearchParams({
      type,
      page: page.toString(),
      page_size: pageSize.toString()
    });

    const response = await fetch(`${API_BASE_URL}/api/invoices?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Token d\'authentification invalide ou expiré');
      }
      if (response.status === 404) {
        throw new Error('L\'API des factures n\'est pas encore disponible sur le serveur');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Erreur HTTP ${response.status}`);
    }

    const data = await response.json();
    console.log('Invoices fetched successfully:', data);
    return data;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

/**
 * Download invoice PDF
 */
export const downloadInvoicePdf = async (
  token: string,
  invoiceId: number
): Promise<Blob> => {
  console.log('Downloading invoice PDF:', invoiceId);
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}/pdf`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Token d\'authentification invalide ou expiré');
      }
      if (response.status === 403) {
        throw new Error('Vous n\'avez pas accès à cette facture');
      }
      if (response.status === 404) {
        throw new Error('Facture introuvable');
      }
      if (response.status === 400) {
        throw new Error('Cette facture n\'est pas disponible au téléchargement');
      }
      throw new Error(`Erreur lors du téléchargement: ${response.status}`);
    }

    const blob = await response.blob();
    console.log('Invoice PDF downloaded successfully');
    return blob;
  } catch (error) {
    console.error('Error downloading invoice PDF:', error);
    throw error;
  }
};

/**
 * Helper function to trigger file download
 */
export const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Status labels for UI display
export const INVOICE_STATUS_LABELS = {
  not_paid: 'Non payée',
  in_payment: 'En cours de paiement',
  partial: 'Partiellement payée',
  paid: 'Payée',
  reversed: 'Annulée'
} as const;

// Type labels for UI display
export const INVOICE_TYPE_LABELS = {
  invoice: 'Facture',
  credit_note: 'Avoir'
} as const;